// frontend/src/components/Motion.js
//
// The two motion primitives the landing page reuses. Both check
// useReducedMotion() and degrade to a plain, fully-visible <div> — a reveal
// that never fires would otherwise leave the whole page invisible for anyone
// who has asked their OS to stop animations.
import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from 'framer-motion';
import theme from '../theme';

const EASE = [0.22, 1, 0.36, 1];

/** Fades and lifts its children the first time they scroll into view. */
// className is forwarded because inline styles cannot hold a media query, and
// these wrappers are often the element that needs to lay out differently on a
// phone. Both branches take it: a reduced-motion visitor gets the same layout.
export function Reveal({ children, delay = 0, y = 30, rotate = 0, duration = 0.75, style, className }) {
  const reduced = useReducedMotion();
  if (reduced) return <div className={className} style={style}>{children}</div>;
  return (
    <motion.div
      className={className}
      style={style}
      initial={{ opacity: 0, y, rotateX: rotate }}
      whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
      viewport={{ once: true, margin: '-70px' }}
      transition={{ duration, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

/** Same, but the children arrive one after another. */
export function RevealStagger({ children, delay = 0, gap = 0.09, y = 30, style }) {
  const reduced = useReducedMotion();
  const items = React.Children.toArray(children);
  if (reduced) return <div style={style}>{children}</div>;
  return (
    <div style={style}>
      {items.map((child, i) => (
        <Reveal key={i} delay={delay + i * gap} y={y}>{child}</Reveal>
      ))}
    </div>
  );
}

/**
 * Tilts toward the pointer in 3D, with a highlight that tracks across the
 * surface. Pointer-driven only: on a touch screen there is no hover to
 * respond to, so the card simply sits flat rather than tilting on tap.
 */
export function Tilt3D({ children, style, max = 10, glare = true }) {
  const reduced = useReducedMotion();
  const ref = useRef(null);

  const px = useMotionValue(0.5);
  const py = useMotionValue(0.5);
  const spring = { stiffness: 150, damping: 18, mass: 0.6 };

  const rotateX = useSpring(useTransform(py, [0, 1], [max, -max]), spring);
  const rotateY = useSpring(useTransform(px, [0, 1], [-max, max]), spring);
  const glareX = useTransform(px, [0, 1], ['0%', '100%']);
  const glareO = useSpring(useMotionValue(0), { stiffness: 120, damping: 20 });

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width);
    py.set((e.clientY - r.top) / r.height);
  };
  const onEnter = () => glareO.set(1);
  const onLeave = () => { px.set(0.5); py.set(0.5); glareO.set(0); };

  if (reduced) return <div style={style}>{children}</div>;

  return (
    <div style={{ perspective: 1000, ...style }}>
      <motion.div
        ref={ref}
        onPointerMove={onMove}
        onPointerEnter={onEnter}
        onPointerLeave={onLeave}
        style={{
          rotateX, rotateY,
          transformStyle: 'preserve-3d',
          position: 'relative',
          height: '100%',
          willChange: 'transform',
        }}
        whileHover={{ scale: 1.015 }}
        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
      >
        {children}
        {glare && (
          <motion.div
            aria-hidden="true"
            style={{
              position: 'absolute', inset: 0, borderRadius: 'inherit',
              pointerEvents: 'none', opacity: glareO, mixBlendMode: 'soft-light',
              background: `linear-gradient(105deg, transparent 35%, ${theme.alpha.white(0.55)} 50%, transparent 65%)`,
              backgroundSize: '260% 100%',
              backgroundPositionX: glareX,
            }}
          />
        )}
      </motion.div>
    </div>
  );
}

/**
 * The hero headline, one word at a time, each rotating up out of the plane.
 * Words rather than characters: splitting Greek to characters breaks
 * accented clusters apart and reads as a glitch, not a flourish.
 */
export function WordsIn({ text, delay = 0, style, wordStyle }) {
  const reduced = useReducedMotion();
  if (reduced) return <span style={style}>{text}</span>;
  const words = String(text).split(' ');
  return (
    <span style={{ display: 'inline-block', perspective: 700, ...style }}>
      {/* The padding/negative-margin pair keeps each clipping box off the
          descenders — without it the mask cuts through ρ, γ and μ. The word
          gap is a margin, because a trailing space inside an overflow:hidden
          inline-block collapses and the words would run together. */}
      {words.map((w, i) => (
        <span key={i} style={{
          display: 'inline-block', overflow: 'hidden', verticalAlign: 'bottom',
          paddingBottom: '0.14em', marginBottom: '-0.14em',
          marginRight: i < words.length - 1 ? '0.26em' : 0,
        }}>
          <motion.span
            style={{ display: 'inline-block', transformOrigin: 'bottom center', ...wordStyle }}
            initial={{ y: '105%', rotateX: 55, opacity: 0 }}
            animate={{ y: '0%', rotateX: 0, opacity: 1 }}
            transition={{ duration: 0.9, delay: delay + i * 0.085, ease: EASE }}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </span>
  );
}
